import React, { useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { kategoriAdi, useKatalog, urunYolu } from "../data/katalog";
import { REHBERLER, rehberBul } from "../data/rehberler";

const WHATSAPP_NUMARA = "905377981129";

/** Kategorinin ilk ürün görseli (yazı kapağı için) */
const useKapaklar = () => {
  const { urunler } = useKatalog();
  return useMemo(() => {
    const k = {};
    for (const r of REHBERLER) k[r.slug] = urunler.find((u) => u.kategori === r.kapakKategori)?.gorsel || null;
    return k;
  }, [urunler]);
};

const useSayfaBilgisi = (baslik, aciklama) => {
  useEffect(() => {
    if (!baslik) return;
    document.title = baslik;
    const meta = document.querySelector('meta[name="description"]');
    const onceki = meta?.getAttribute("content");
    if (aciklama) meta?.setAttribute("content", aciklama);
    return () => {
      if (meta && onceki != null) meta.setAttribute("content", onceki);
    };
  }, [baslik, aciklama]);
};

const TurkceNotu = ({ t, lang }) =>
  lang !== "tr" ? <p className="mb-6 rounded-lg bg-canvas px-4 py-3 text-[13px] text-muted">{t("guide.turkishOnly")}</p> : null;

/** /rehber — yazı listesi */
export function RehberListesi() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "tr").split("-")[0];
  const kapaklar = useKapaklar();
  useSayfaBilgisi(`${t("guide.title")} — ${t("siteName")}`, t("guide.meta"));

  return (
    <section className="container-page section">
      <div className="page-head">
        <p className="eyebrow">{t("guide.eyebrow")}</p>
        <h1 className="page-title mt-3">{t("guide.title")}</h1>
        <p className="mt-3 max-w-2xl text-[15px] leading-relaxed text-body">{t("guide.subtitle")}</p>
      </div>
      <TurkceNotu t={t} lang={lang} />
      <ul className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {REHBERLER.map((r) => (
          <li key={r.slug}>
            <Link
              to={`/rehber/${r.slug}`}
              className="group flex h-full flex-col overflow-hidden rounded-2xl border border-line bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_-18px_rgba(0,0,0,0.3)]"
            >
              <span className="relative block aspect-[16/9] bg-canvas">
                {kapaklar[r.slug] && (
                  <img src={kapaklar[r.slug]} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-contain p-6 transition-transform duration-500 group-hover:scale-105" />
                )}
              </span>
              <span className="flex flex-1 flex-col p-5">
                <span className="text-[11px] font-semibold uppercase tracking-eyebrow text-accent">{t("guide.readTime", { dk: r.okuma })}</span>
                <span className="mt-2 text-[17px] font-semibold leading-snug text-ink">{r.baslik}</span>
                <span className="mt-2 flex-1 text-[13.5px] leading-relaxed text-body">{r.ozet}</span>
                <span className="link-arrow mt-4 text-[13px]">
                  {t("guide.read")}
                  <span aria-hidden="true" className="arrow-forward" />
                </span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** /rehber/:slug — yazı */
export default function RehberYazisi() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "tr").split("-")[0];
  const { urunler, adlar } = useKatalog();
  const kapaklar = useKapaklar();
  const r = rehberBul(slug);

  useSayfaBilgisi(r ? `${r.baslik} — ${t("siteName")}` : null, r?.ozet);

  // Arama motorları için Article verisi
  useEffect(() => {
    if (!r) return;
    const etiket = document.createElement("script");
    etiket.type = "application/ld+json";
    etiket.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: r.baslik,
      description: r.ozet,
      inLanguage: "tr",
      publisher: { "@type": "Organization", name: "Akkuzey Yapı Malzemeleri" },
    });
    document.head.appendChild(etiket);
    return () => etiket.remove();
  }, [r]);

  // Yazıdaki kategorilerden sırayla birer ürün (dengeli dağılım), en fazla 8
  const urunlerListesi = useMemo(() => {
    if (!r) return [];
    const gruplar = r.urunKategorileri.map((k) => urunler.filter((u) => u.kategori === k));
    const secilen = [];
    for (let i = 0; secilen.length < 8 && i < 8; i++)
      for (const g of gruplar) if (g[i] && secilen.length < 8) secilen.push(g[i]);
    return secilen;
  }, [r, urunler]);

  if (!r) {
    return (
      <section className="container-page section text-center">
        <h1 className="page-title">{t("guide.notFound")}</h1>
        <Link to="/rehber" className="btn-primary mt-8 inline-flex">{t("guide.all")}</Link>
      </section>
    );
  }

  const digerleri = REHBERLER.filter((x) => x.slug !== r.slug).slice(0, 3);
  const bolumId = (i) => `bolum-${i + 1}`;

  return (
    <main>
      <article className="container-page pb-6 pt-10">
        <nav aria-label="Breadcrumb" className="mb-6 flex items-center gap-2 text-[13px] text-muted">
          <Link to="/rehber" className="shrink-0 hover:text-ink">{t("guide.menu")}</Link>
          <span aria-hidden="true" className="text-line">/</span>
          <span className="truncate text-ink">{r.baslik}</span>
        </nav>

        <div className="grid gap-10 lg:grid-cols-[1fr_300px]">
          <div className="min-w-0">
            <TurkceNotu t={t} lang={lang} />
            <p className="text-[12px] font-semibold uppercase tracking-eyebrow text-accent">{t("guide.readTime", { dk: r.okuma })}</p>
            <h1 className="mt-3 text-[28px] font-semibold leading-tight tracking-[-0.02em] text-ink sm:text-[36px]">{r.baslik}</h1>
            <p className="mt-4 text-[17px] leading-relaxed text-body">{r.ozet}</p>

            {kapaklar[r.slug] && (
              <div className="relative mt-8 aspect-[16/7] overflow-hidden rounded-2xl border border-line bg-white">
                <img src={kapaklar[r.slug]} alt="" className="absolute inset-0 h-full w-full object-contain p-8" />
              </div>
            )}

            <div className="mt-10 space-y-10">
              {r.bolumler.map((b, i) => (
                <section key={b.baslik} id={bolumId(i)} className="scroll-mt-40">
                  <h2 className="text-[21px] font-semibold tracking-tight text-ink">{b.baslik}</h2>
                  {(b.paragraflar || []).map((p) => (
                    <p key={p.slice(0, 30)} className="mt-4 text-[15.5px] leading-[1.8] text-body">{p}</p>
                  ))}
                  {b.liste && (
                    <ul className="mt-4 space-y-2.5">
                      {b.liste.map((m) => (
                        <li key={m.slice(0, 30)} className="flex gap-3 text-[15px] leading-relaxed text-body">
                          <span aria-hidden="true" className="mt-[10px] h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                          <span>{m}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              ))}
            </div>
          </div>

          {/* Kenar: içindekiler + bağlantılar */}
          <aside className="space-y-6 lg:sticky lg:top-40 lg:self-start">
            <nav className="rounded-2xl border border-line bg-white p-5">
              <p className="mb-3 text-[11px] font-semibold uppercase tracking-eyebrow text-muted">{t("guide.contents")}</p>
              <ol className="space-y-2 text-[13.5px]">
                {r.bolumler.map((b, i) => (
                  <li key={b.baslik}>
                    <a href={`#${bolumId(i)}`} className="text-body transition-colors hover:text-ink">
                      {i + 1}. {b.baslik}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>
            <div className="rounded-2xl bg-ink p-5 text-white">
              <p className="text-[11px] font-semibold uppercase tracking-eyebrow text-white/60">{t("guide.usefulLinks")}</p>
              <ul className="mt-3 space-y-2">
                {r.baglantilar.map((b) => (
                  <li key={b.yol}>
                    <Link to={b.yol} className="flex items-center justify-between gap-2 rounded-lg bg-white/5 px-3 py-2.5 text-[13.5px] text-white/90 transition-colors hover:bg-white/10">
                      {b.etiket}
                      <span aria-hidden="true" className="arrow-forward" />
                    </Link>
                  </li>
                ))}
              </ul>
              <a
                href={`https://wa.me/${WHATSAPP_NUMARA}?text=${encodeURIComponent(t("guide.whatsapp", { baslik: r.baslik }))}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 flex items-center justify-center rounded-md bg-[#25D366] px-4 py-2.5 text-[13.5px] font-semibold text-white hover:bg-[#1ebe5b]"
              >
                {t("guide.askExpert")}
              </a>
            </div>
          </aside>
        </div>
      </article>

      {/* Yazıdaki ürün grupları */}
      {urunlerListesi.length > 0 && (
        <section className="container-page py-10">
          <h2 className="mb-5 text-[20px] font-semibold tracking-tight text-ink">{t("guide.products")}</h2>
          <ul className="scrollbar-hide -mx-4 flex snap-x gap-3 overflow-x-auto px-4 pb-2 sm:mx-0 sm:grid sm:grid-cols-4 sm:overflow-visible sm:px-0">
            {urunlerListesi.map((u) => (
              <li key={u.id} className="w-40 shrink-0 snap-start sm:w-auto">
                <Link to={urunYolu(u)} className="group flex h-full flex-col overflow-hidden rounded-xl border border-line bg-white transition-all hover:-translate-y-0.5 hover:shadow-md">
                  <span className="relative block aspect-square">
                    <img src={u.gorsel} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-contain p-3" />
                  </span>
                  <span className="block border-t border-line/70 px-3 py-2.5">
                    <span className="block text-[10.5px] font-semibold uppercase tracking-eyebrow text-muted">{u.marka}</span>
                    <span className="mt-0.5 line-clamp-2 block text-[12.5px] font-medium leading-snug text-ink">{u.ad}</span>
                    <span className="mt-1 block truncate text-[11px] text-muted">{kategoriAdi(adlar, u.kategori, lang)}</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Diğer yazılar */}
      <section className="border-t border-line bg-canvas/50">
        <div className="container-page py-12">
          <h2 className="mb-5 text-[18px] font-semibold text-ink">{t("guide.more")}</h2>
          <ul className="grid gap-4 sm:grid-cols-3">
            {digerleri.map((d) => (
              <li key={d.slug}>
                <Link to={`/rehber/${d.slug}`} className="block h-full rounded-xl border border-line bg-white p-5 transition-colors hover:border-ink/30">
                  <span className="text-[15px] font-semibold leading-snug text-ink">{d.baslik}</span>
                  <span className="mt-2 block text-[13px] leading-relaxed text-body">{d.ozet}</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </main>
  );
}
